// LLM generated - Model Claude 4 Sonnet
import { useQueries } from '@tanstack/react-query';
import { STALE_TIME } from '~/domains/contest/constants/query';
import { getMessageReactions } from '~/services/live-activity/live-activity-service.service.api';
import type { GetMessageReactionsResponse } from '~/services/live-activity/types';
import { logError } from '~/common/utils/log';
import { createQueryKey } from '../constants/queryKeys';

type UsePostsReactionsProps = {
  chatId?: string;
  messageIds: string[];
  currentUserId?: string;
  enabled?: boolean;
};

export function usePostsReactions({
  chatId,
  messageIds,
  currentUserId,
  enabled = true,
}: UsePostsReactionsProps) {
  // Fetch reactions for all post messages efficiently using useQueries
  // Only create queries when chatId is defined
  const reactionQueries = useQueries({
    queries: chatId
      ? messageIds.map((messageId) => ({
          queryKey: createQueryKey.messageReactions(chatId, messageId),
          queryFn: () => getMessageReactions(chatId, messageId, { limit: 20 }),
          enabled: enabled && !!messageId,
          staleTime: STALE_TIME,
        }))
      : [], // Empty array when chatId is undefined - no queries will be created
  });

  // Process the results to create a map of messageId -> userReactions for current user
  const userReactionsMap: Record<string, string[]> = {};
  const isLoading = reactionQueries.some((query) => query.isLoading);
  const hasError = reactionQueries.some((query) => query.error);

  reactionQueries.forEach((query, index) => {
    const messageId = messageIds[index];
    const reactionsData = query.data as GetMessageReactionsResponse | undefined;

    // Log errors for debugging
    if (query.error) {
      logError('usePostsReactions - API error:', {
        messageId,
        chatId,
        error: query.error,
        endpoint: `/chats/${chatId}/messages/${messageId}/reactions`,
      });
    }

    if (reactionsData && currentUserId) {
      // Find the current user's reactions in the response by user ID
      const currentUserReaction = reactionsData.data.find(
        (reaction) => reaction.user.id === currentUserId
      );

      userReactionsMap[messageId] = currentUserReaction?.reactionCodes || [];
    } else {
      userReactionsMap[messageId] = [];
    }
  });

  return {
    userReactionsMap,
    isLoading,
    hasError,
    queries: reactionQueries,
  };
}
